import { Card } from "@prisma/ui";
import { useQuery } from "@tanstack/react-query";
import { Search, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { RequestStatusChip } from "../../components/Status";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";

type Row = components["schemas"]["InboxRowOut"];
type Product = components["schemas"]["ProductOut"];
const TABS = ["new", "need_clarification", "approved", "rejected", "unassigned", "all"] as const;
type Tab = (typeof TABS)[number];
const PRIORITIES = ["NORMAL", "URGENT", "CRITICAL"] as const;
const COLUMNS = ["request", "product", "marketer", "objective", "deliverables", "deadline", "priority", "status"] as const;
const OBJECTIVES = ["SALES_CONVERSION", "AWARENESS", "RETARGETING", "LAUNCH", "ENGAGEMENT", "OTHER"] as const;

/** Request Inbox (core doc 04 P2): tabs, filters and the request table, scoped server-side. */
export function RequestsPage() {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(params.get("tab") ?? "") ? (params.get("tab") as Tab) : "new";
  const [text, setText] = useState(params.get("q") ?? "");
  const filters = {
    tab, product: params.get("product") || undefined,
    priority: (params.get("priority") || undefined) as (typeof PRIORITIES)[number] | undefined,
    objective: params.get("objective") || undefined, q: params.get("q") || undefined,
  };
  const rows = useQuery({
    queryKey: ["requests", filters],
    queryFn: () => call<Row[]>(api.GET("/app/v1/requests", { params: { query: filters } })),
  });
  const products = useQuery({ queryKey: ["products"], queryFn: () => call<Product[]>(api.GET("/app/v1/products")),
    enabled: Boolean(me.permissions["product.view"]) });
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const select = "h-10 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink";

  return (
    <div className="space-y-6">
      <PageHeader titleKey="requests.title" subtitle={t("requests.subtitle")} />
      <div role="tablist" aria-label={t("requests.tabs_label")} className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((k) => (
          <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => set("tab", k)}
            className="-mb-px border-b-2 border-transparent px-4 py-2.5 text-[14px] text-ink-2 hover:text-ink aria-selected:border-[var(--accent-primary)] aria-selected:font-semibold aria-selected:text-ink">
            {t(`requests.tab_${k}`)}
          </button>
        ))}
      </div>
      <form className="flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); set("q", text.trim() || null); }}>
        <label className="relative">
          <span className="sr-only">{t("requests.search")}</span>
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-2" />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("requests.search")}
            className="h-10 w-72 rounded-[var(--radius-control)] border border-line bg-surface pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-2" />
        </label>
        <label className="sr-only" htmlFor="f-product">{t("requests.col_product")}</label>
        <select id="f-product" className={select} value={filters.product ?? ""} onChange={(e) => set("product", e.target.value || null)}>
          <option value="">{t("requests.all_products")}</option>
          {(products.data ?? []).map((p) => <option key={p.id} value={p.code}>{p.name}</option>)}
        </select>
        <label className="sr-only" htmlFor="f-priority">{t("requests.col_priority")}</label>
        <select id="f-priority" className={select} value={filters.priority ?? ""} onChange={(e) => set("priority", e.target.value || null)}>
          <option value="">{t("requests.all_priorities")}</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{t(`requests.priority_${p.toLowerCase()}`)}</option>)}
        </select>
        <label className="sr-only" htmlFor="f-objective">{t("requests.col_objective")}</label>
        <select id="f-objective" className={select} value={filters.objective ?? ""} onChange={(e) => set("objective", e.target.value || null)}>
          <option value="">{t("requests.all_objectives")}</option>
          {OBJECTIVES.map((o) => <option key={o} value={o}>{t(`requests.objective_${o.toLowerCase()}`)}</option>)}
        </select>
      </form>
      <QueryState query={rows} emptyKey="requests.empty">
        {(list) => (
          <Card className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-left text-[14px]">
              <thead className="text-[12px] uppercase tracking-wide text-ink-2">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c} scope="col" className="px-4 py-3 font-medium">{t(`requests.col_${c}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.display_id} className="border-t border-line hover:bg-elevated">
                    <td className="px-4 py-3">
                      <Link to={`/requests/${r.display_id}`} className="font-semibold text-ink hover:underline">{r.display_id}</Link>
                      {r.assigned_cp_id === null && (
                        <div className="text-[12px] text-[var(--status-warning)]">{t("requests.unassigned")}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink">
                      {r.product_name}
                      <div className="text-[12px] text-ink-2">{r.campaign_id ?? "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-ink-2">{r.marketer_name}</td>
                    <td className="px-4 py-3 text-ink-2">{t(`requests.objective_${r.objective.toLowerCase()}`)}</td>
                    <td className="px-4 py-3 text-ink-2">
                      {r.deliverables.map((d) => t(`requests.deliverable_n_${d.type.toLowerCase()}`, { count: d.quantity })).join(" · ")}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {formatDate(r.deadline, i18n.language, me.time_zone)}
                      {r.deadline_risk && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-[var(--status-warning)]">
                          <TriangleAlert aria-hidden="true" className="size-3.5" />{t("requests.deadline_risk")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">{t(`requests.priority_${r.priority.toLowerCase()}`)}</td>
                    <td className="px-4 py-3"><RequestStatusChip status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </QueryState>
    </div>
  );
}
